import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { User, MessageSquare, FileText, Calendar, Award, ArrowLeft, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';

const UserProfile = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, activityRes] = await Promise.all([
        axios.get(`${API}/users/${userId}/profile`),
        axios.get(`${API}/users/${userId}/activity`)
      ]);
      setProfile(profileRes.data);
      setActivity(activityRes.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'post':
        return <FileText className="text-[#C5A059]" size={16} />;
      case 'comment':
        return <MessageSquare className="text-[#007AFF]" size={16} />;
      default:
        return <FileText className="text-gray-400" size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Profile Not Found</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <Link to="/forum">
              <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
                <ArrowLeft size={16} className="mr-2" />
                Back to Forum
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isOwnProfile = currentUser?.user_id === profile?.user_id;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Profile Header */}
          <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-white/10 rounded-lg p-8 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Avatar */}
              <div className="relative">
                {profile.picture ? (
                  <img
                    src={profile.picture}
                    alt={profile.name}
                    className="w-24 h-24 rounded-full border-4 border-[#C5A059]/30"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
                    <span className="text-black text-3xl font-bold">
                      {profile.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                )}
                {profile.role === 'admin' && (
                  <div className="absolute -bottom-1 -right-1 bg-[#C5A059] text-black text-xs font-bold px-2 py-0.5 rounded-full">
                    ADMIN
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-manrope font-bold text-3xl text-white mb-2" data-testid="profile-name">
                  {profile.name}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>Joined {formatDate(profile.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Award size={14} className="text-[#C5A059]" />
                    <span>{profile.reputation} reputation</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{profile.post_count}</p>
                  <p className="text-gray-500 text-sm">Posts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{profile.comment_count}</p>
                  <p className="text-gray-500 text-sm">Comments</p>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Tabs */}
          <Tabs defaultValue="activity" className="space-y-6">
            <TabsList className="bg-[#111111] border border-white/10 p-1">
              <TabsTrigger 
                value="activity" 
                className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black"
              >
                Recent Activity
              </TabsTrigger>
              <TabsTrigger 
                value="posts" 
                className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black"
              >
                Posts
              </TabsTrigger>
              <TabsTrigger 
                value="comments" 
                className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white"
              >
                Comments
              </TabsTrigger>
            </TabsList>

            {/* All Activity */}
            <TabsContent value="activity">
              {activity.length === 0 ? (
                <div className="bg-[#111111] border border-white/10 rounded-lg p-8 text-center">
                  <User className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400">No activity yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activity.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="block bg-[#111111] border border-white/10 rounded-lg p-4 hover:border-[#C5A059]/50 transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">{getActivityIcon(item.activity_type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold uppercase ${
                              item.activity_type === 'post' ? 'text-[#C5A059]' : 'text-[#007AFF]'
                            }`}>
                              {item.activity_type}
                            </span>
                            <span className="text-gray-600">•</span>
                            <span className="text-gray-500 text-xs flex items-center gap-1">
                              <Clock size={12} />
                              {formatDate(item.created_at)}
                            </span>
                          </div>
                          <h3 className="font-medium text-white group-hover:text-[#C5A059] transition-colors truncate">
                            {item.title}
                          </h3>
                          <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                            {item.content_preview}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Posts Only */}
            <TabsContent value="posts">
              {activity.filter(a => a.activity_type === 'post').length === 0 ? (
                <div className="bg-[#111111] border border-white/10 rounded-lg p-8 text-center">
                  <FileText className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400">No posts yet</p>
                  {isOwnProfile && (
                    <Link to="/forum/create">
                      <Button className="mt-4 bg-[#C5A059] text-black hover:bg-[#D4AF37]">
                        Create Your First Post
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {activity.filter(a => a.activity_type === 'post').map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="block bg-[#111111] border border-white/10 rounded-lg p-4 hover:border-[#C5A059]/50 transition-all group"
                    >
                      <h3 className="font-medium text-white group-hover:text-[#C5A059] transition-colors mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{item.content_preview}</p>
                      <p className="text-gray-500 text-xs mt-2">{formatDate(item.created_at)}</p>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Comments Only */}
            <TabsContent value="comments">
              {activity.filter(a => a.activity_type === 'comment').length === 0 ? (
                <div className="bg-[#111111] border border-white/10 rounded-lg p-8 text-center">
                  <MessageSquare className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400">No comments yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activity.filter(a => a.activity_type === 'comment').map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="block bg-[#111111] border border-white/10 rounded-lg p-4 hover:border-[#007AFF]/50 transition-all group"
                    >
                      <p className="text-gray-400 text-sm line-clamp-3">{item.content_preview}</p>
                      <p className="text-gray-500 text-xs mt-2">{formatDate(item.created_at)}</p>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UserProfile;
